import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { func, number, oneOf, oneOfType, string } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import { MDialog } from 'common';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import {
  useGetCustomerAuthorizationQuery,
  useGetViewCustomerAuthorizationQuery,
} from '../customerAuthorizationsEndpoints';
import CustomerInformation from '../edit/CustomerInformation';
import useCustomerAuthorizationEditor from '../edit/useCustomerAuthorizationEditor';
import CustomerAuthorizationTabs from './tabs/CustomerAuthorizationTabs';
import CustomerInfoSection from './customer-info/CustomerInfoSection';
import CustomerInformationAccordion from '../components/CustomerInformationAccordion';

const DRAWER_TITLES = {
  edit: 'Edit Authorization',
  view: 'View Authorization',
};

// Base shell drawer for the Edit Customer Authorization flow.
// Additional sections/fields will be added incrementally in follow-up work.
const EditCustomerAuthorizationDrawer = ({
  customerAuthorizationId,
  mode,
  onEdit,
  setCustomerAuthorizationId,
}) => {
  const { isProductLoadingManage, isSupplier } = useUserRolePermissions();
  const isCustomerInfoSectionEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_EDIT_CUSTOMER_AUTHORIZATION_CUSTOMER_INFO_SECTION
  );

  const isEditMode = mode === 'edit';
  const params = { customerAuthorizationId };
  const { data: editCustomerAuthorization = {}, isFetching: isFetchingEdit } =
    useGetCustomerAuthorizationQuery(
      { params },
      {
        refetchOnMountOrArgChange: true,
        skip: R.or(R.isNil(customerAuthorizationId), !isEditMode),
      }
    );
  const { data: viewCustomerAuthorization = {}, isFetching: isFetchingView } =
    useGetViewCustomerAuthorizationQuery(
      { params },
      {
        refetchOnMountOrArgChange: true,
        skip: R.or(R.isNil(customerAuthorizationId), isEditMode),
      }
    );
  const customerAuthorization = isEditMode ? editCustomerAuthorization : viewCustomerAuthorization;
  const isFetching = isEditMode ? isFetchingEdit : isFetchingView;
  const {
    customerAuth,
    enableSave,
    handleClose: handleEditClose,
    handleDiscard,
    handleSave,
    hasErrors,
    isSaving,
    openDiscardDialog,
    setCustomerAuth,
    setOpenDiscardDialog,
    setValidation,
    validation,
  } = useCustomerAuthorizationEditor({
    customerAuthorization,
    isEditMode,
    onClose: () => {
      setCustomerAuthorizationId(null);
    },
  });
  const handleClose = isEditMode
    ? handleEditClose
    : () => {
        setCustomerAuthorizationId(null);
        setCustomerAuth({});
      };
  const displayedCustomerAuth = R.isEmpty(customerAuth) ? customerAuthorization : customerAuth;
  const getTerminalIds = authorization =>
    (authorization.terminals ?? [])
      .map(R.prop('terminalId'))
      .sort((left, right) => Number(left) - Number(right));
  const getProductIds = authorization =>
    (authorization.products ?? [])
      .map(R.prop('productId'))
      .sort((left, right) => Number(left) - Number(right));
  const terminalsError = isEditMode && R.isEmpty(displayedCustomerAuth.terminals ?? []);
  const hasTerminalChanges = R.not(
    R.equals(getTerminalIds(customerAuthorization), getTerminalIds(displayedCustomerAuth))
  );
  const productsError =
    isEditMode &&
    displayedCustomerAuth.authorizationType === 'SPECIFIC' &&
    R.isEmpty(displayedCustomerAuth.products ?? []);
  const hasProductChanges = R.not(
    R.equals(getProductIds(customerAuthorization), getProductIds(displayedCustomerAuth))
  );

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={R.not(R.isNil(customerAuthorizationId))}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: 1, lg: 760 },
          },
        },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          boxShadow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          pb: '14px',
          pt: 1.5,
          px: 3,
          zIndex: 1,
        }}
      >
        <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
          {DRAWER_TITLES[mode]}
        </Typography>
        <IconButton
          aria-label="closeIcon"
          onClick={handleClose}
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          <CloseRoundedIcon sx={{ color: 'primary.light' }} />
        </IconButton>
      </Box>
      {isFetching || isSaving ? (
        <CircularProgress
          size={48}
          sx={{
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            left: { xs: 'calc(50% - 24px)', lg: 'calc(50% - 32px)' },
            position: 'absolute',
            top: 'calc(50% - 64px)',
          }}
        />
      ) : (
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            flex: 1,
            minHeight: 0,
            msOverflowStyle: 'none',
            overflowY: 'auto',
            pb: 2,
            pt: 2,
            px: 3,
            scrollbarWidth: 'none',
          }}
        >
          <CustomerInfoSection
            customerAuth={displayedCustomerAuth}
            isCustomerInfoSectionEnabled={isCustomerInfoSectionEnabled}
            isEditMode={isEditMode}
            originalCustomerAuth={customerAuthorization}
            setCustomerAuth={setCustomerAuth}
            setValidation={setValidation}
            validation={validation}
          >
            {isEditMode ? (
              <CustomerInformation
                customerAuth={displayedCustomerAuth}
                setCustomerAuth={setCustomerAuth}
                setValidation={setValidation}
                validation={validation}
              />
            ) : (
              <CustomerInformationAccordion
                customerAuthorization={customerAuthorization}
                defaultExpanded
              />
            )}
          </CustomerInfoSection>
          <CustomerAuthorizationTabs
            customerAuth={displayedCustomerAuth}
            hasProductChanges={hasProductChanges}
            hasTerminalChanges={hasTerminalChanges}
            isEditMode={isEditMode}
            mode={mode}
            productsError={productsError}
            setCustomerAuth={setCustomerAuth}
            setValidation={setValidation}
            terminalsError={terminalsError}
            validation={validation}
          />
        </Box>
      )}
      {isEditMode && !isFetching && !isSaving && (
        <Stack
          spacing={1}
          sx={{
            alignItems: 'center',
            backgroundColor: 'surface.white',
            boxShadow: 0,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            flexDirection: { xs: 'column', lg: 'row' },
            flexShrink: 0,
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            zIndex: 1,
          }}
        >
          <Typography sx={{ color: 'error.main', pt: '2px' }} variant="caption">
            {hasErrors && 'Please remove errors before continuing'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClose}
              sx={{
                mr: 2,
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={!enableSave}
              onClick={handleSave}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      )}
      {!isEditMode && (
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'surface.white',
            boxShadow: 0,
            display: 'flex',
            flexShrink: 0,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            justifyContent: { xs: 'center', lg: 'flex-end' },
            px: 3,
            py: 2,
            zIndex: 1,
          }}
        >
          {isProductLoadingManage && isSupplier ? (
            <Button
              onClick={() => onEdit(customerAuthorizationId)}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
            >
              Edit
            </Button>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </Box>
      )}
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => setOpenDiscardDialog(false)}
        leftButtonText="Keep Editing"
        onClose={() => setOpenDiscardDialog(false)}
        open={openDiscardDialog}
        rightButtonAction={handleDiscard}
        rightButtonText="Continue"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved customer authorization changes.
        </Typography>
      </MDialog>
    </Drawer>
  );
};

EditCustomerAuthorizationDrawer.propTypes = {
  customerAuthorizationId: oneOfType([number, string]),
  mode: oneOf(['edit', 'view']),
  onEdit: func.isRequired,
  setCustomerAuthorizationId: func.isRequired,
};

EditCustomerAuthorizationDrawer.defaultProps = {
  customerAuthorizationId: null,
  mode: 'edit',
};

export default EditCustomerAuthorizationDrawer;
